import React from 'react';
import Input from 'formik-antd/lib/input';
import Select from 'formik-antd/lib/select';
import { Switch } from './Switch';
import Form from 'formik-antd/lib/form';

import { TFunction } from 'i18next';
import {
  Flex,
  KeyValue,
  Grid,
  Segment,
  FormGroup,
  RemoteAccessSSHProfile,
  sshValidationSchema,
} from '@mss/mss-component-library';
import { Formik, ErrorMessage } from 'formik';
import emptyProfiles from './emptyProfiles';
import { useTranslation } from 'react-i18next';
import SubmitButton from 'jsx/common/SubmitButton';
import Button from 'jsx/common/Button';
import { Trash } from 'react-feather';
import ToggleEnabled from './ToggleEnabled';
import { ProfileCollapse } from './ProfileCollapse';

const colorschemes = (t: TFunction): KeyValue[] => [
  { value: 'black-white', key: t('Black & white') },
  { value: 'gray-black', key: t('Gray & black') },
  { value: 'green-black', key: t('Green & black') },
  { value: 'white-black', key: t('White & black') },
];
type SSHCollapseProps = {
  onSubmit: (values: any) => any;
  profile?: RemoteAccessSSHProfile & { id: string };
  asset: string;
  deleteProfile?: (id: string) => void;
  readonly?: boolean;
};

const SSHCollapse = ({
  onSubmit,
  profile,
  asset,
  deleteProfile,
  readonly,
}: SSHCollapseProps) => {
  const [t] = useTranslation(['remoteAccess', 'common', 'asset']);
  const toggleEnabled = () => {
    onSubmit({ ...profile, enabled: !profile?.enabled });
  };
  const initialValues = profile
    ? { ...emptyProfiles.ssh, ...profile, asset }
    : { ...emptyProfiles.ssh, asset };
  initialValues.settings.private_key = '';

  return (
    <Formik
      validationSchema={sshValidationSchema(t)}
      onSubmit={onSubmit}
      initialValues={
        profile
          ? { ...emptyProfiles.ssh, ...profile, asset }
          : { ...emptyProfiles.ssh, asset }
      }
    >
      {({ values, isSubmitting, errors, setFieldValue, setFieldTouched }) => {
        const hasErrors = Object.keys(errors).length > 0;
        return (
          <ProfileCollapse
            defaultActiveKey={profile ? undefined : 'ssh'}
            bordered={false}
            expandIconPosition="end"
          >
            <ProfileCollapse.Panel
              key="ssh"
              header={
                <span>
                  {t('SSH')} - {values.name}{' '}
                  {!profile && (
                    <strong style={{ color: 'red' }}>{t('Unsaved')}</strong>
                  )}
                </span>
              }
              extra={
                <ToggleEnabled
                  setFieldValue={setFieldValue}
                  t={t}
                  checked={values.enabled}
                  onSubmit={profile ? toggleEnabled : undefined}
                  disabled={readonly}
                />
              }
            >
              <Form>
                <Grid.Container>
                  <Segment
                    heading={
                      <Flex v="center" h={deleteProfile ? 'between' : 'start'}>
                        <span>{t('Connection Settings')}</span>
                        {deleteProfile && profile && (
                          <Button
                            size="small"
                            onClick={() => deleteProfile(profile.id)}
                            data-ta="SSHCollapseDeleteButton"
                          >
                            <Flex v="center">
                              <Trash size={14} className="mr-1" />
                              {t('Delete')}
                            </Flex>
                          </Button>
                        )}
                      </Flex>
                    }
                  >
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="name" />}
                          label={t('Name')}
                          required
                          input={
                            <Input
                              disabled={readonly}
                              name="name"
                              value={values.name}
                              onInput={() =>
                                setFieldTouched(`name`, true, true)
                              }
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.port" />}
                          label={t('Port')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.port"
                              type="number"
                              value={values.settings.port || ''}
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.host-key" />}
                          label={t('Host key')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.host-key"
                              value={values.settings['host-key'] || ''}
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Authentication Settings')}>
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.username" />}
                          label={t('Username')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.username"
                              value={values.settings.username}
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.password" />}
                          label={t('Password')}
                          input={
                            <Input.Password
                              disabled={readonly}
                              name="settings.password"
                              defaultValue={
                                values.settings_meta.secrets.password
                                  ? '*******************'
                                  : ''
                              }
                              allowClear={true}
                              visibilityToggle={true}
                              onChange={val => {
                                setFieldValue(
                                  'settings.password',
                                  val.target.value
                                );
                              }}
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.passphrase" />}
                          label={t('Passphrase')}
                          input={
                            <Input.Password
                              disabled={readonly}
                              name="settings.passphrase"
                              defaultValue={
                                values.settings_meta.secrets.passphrase
                                  ? '*******************'
                                  : ''
                              }
                              allowClear={true}
                              visibilityToggle={true}
                              onChange={val => {
                                setFieldValue(
                                  'settings.passphrase',
                                  val.target.value
                                );
                              }}
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={12}>
                        <FormGroup
                          error={<ErrorMessage name="settings.private_key" />}
                          label={t('Private key')}
                          input={
                            <Input.TextArea
                              disabled={readonly}
                              rows={6}
                              name="settings.private_key"
                              value={values.settings['private_key']}
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Session Recording Settings')}>
                    <Grid.Row>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Record session')}
                          input={<Switch name="record_session" size="small" />}
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Exclude output')}
                          input={
                            <Switch
                              name="settings.recording-exclude-output"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Exclude mouse')}
                          input={
                            <Switch
                              name="settings.recording-exclude-mouse"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={3}>
                        <FormGroup
                          label={t('Include keys')}
                          input={
                            <Switch
                              name="settings.recording-include-keys"
                              disabled={!values.record_session}
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Display')}>
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.color_scheme" />}
                          label={t('Color scheme')}
                          input={
                            <Select
                              disabled={readonly}
                              name="settings.color_scheme"
                              style={{ width: '100%' }}
                              placeholder={`${t('Please select one')}...`}
                            >
                              {colorschemes(t).map(colorscheme => (
                                <Select.Option
                                  key={colorscheme.key}
                                  value={colorscheme.value}
                                >
                                  {colorscheme.key}
                                </Select.Option>
                              ))}
                            </Select>
                          }
                        />
                      </Grid.Col>

                      {/* <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.read-only" />}
                          label={t('Read only')}
                          input={
                            <Switch disabled={readonly} name="settings.read-only" size="small" />
                          }
                        />
                      </Grid.Col> */}
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Clipboard')}>
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.disable-copy" />}
                          label={t('Disable copy from remote')}
                          input={
                            <Switch
                              disabled={readonly}
                              name="settings.disable-copy"
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.disable-paste" />}
                          label={t('Disable paste from client')}
                          input={
                            <Switch
                              disabled={readonly}
                              name="settings.disable-paste"
                              size="small"
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Segment heading={t('Terminal behavior')}>
                    <Grid.Row>
                      <Grid.Col width={4}>
                        <FormGroup
                          error={<ErrorMessage name="settings.backspace" />}
                          label={t('Backspace key binding')}
                          input={
                            <Input
                              disabled={readonly}
                              name="settings.backspace"
                              value={values.settings.backspace}
                            />
                          }
                        />
                      </Grid.Col>
                    </Grid.Row>
                  </Segment>
                  <Grid.Row>
                    <Grid.Col width={12} className="mt-4">
                      <Flex h="end" v="center">
                        <SubmitButton
                          loading={isSubmitting}
                          disabled={
                            isSubmitting ||
                            hasErrors ||
                            !values.name ||
                            readonly
                          }
                        >
                          {t('Save profile')}
                        </SubmitButton>
                      </Flex>
                    </Grid.Col>
                  </Grid.Row>
                </Grid.Container>
              </Form>
            </ProfileCollapse.Panel>
          </ProfileCollapse>
        );
      }}
    </Formik>
  );
};

export default SSHCollapse;
